import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, qs, type BillingItem, type BillingQueue, type Client, type Paged } from '../api';
import { Dialog, Empty, ErrorAlert, Field, HowTo, Loading } from '../components/ui';
import { date, hm, money } from '../format';

const QUEUE_LABEL: Record<BillingQueue, string> = {
  pendente_conferencia: 'Pendente de conferência',
  pronto_envio: 'Pronto para envio',
  enviado: 'Enviado / aguardando',
  falha: 'Falha de transferência',
  faturado: 'Faturado',
  cancelado: 'Cancelado',
};

const SOURCE_LABEL: Record<BillingItem['sourceType'], string> = {
  execution: 'Atendimento avulso',
  overage: 'Excedente autorizado',
  contract_initial: 'Contratação de banco',
  contract_addition: 'Ampliação de banco',
  monthly: 'Mensalidade',
  installment: 'Parcela',
};

const TRANSPORT_LABEL = { nao_enviado: 'não enviado', aguardando: 'aguardando', enviado: 'enviado', falhou: 'falhou' } as const;
const BILLING_LABEL = { nao_faturado: 'não faturado', faturado: 'faturado', cancelado: 'cancelado', ajustado: 'ajustado' } as const;

interface ListResponse extends Paged<BillingItem> {
  totals: { count: number; total: string; byClient: { clientId: string; clientName: string; n: number; total: string }[] };
  queues: Record<BillingQueue, number>;
}

function originLink(i: BillingItem) {
  if (i.sourceType === 'execution' || i.sourceType === 'overage') return `/execucoes/${i.sourceId}`;
  if (i.sourceType === 'contract_initial' || i.sourceType === 'contract_addition') return `/bancos-de-horas/${i.contractId}`;
  return `/contratos/${i.contractId}`;
}

interface TransferResult { itemId: string; ok: boolean; status: string; message: string; reference?: string }

// Faturamento e integração (seção 10). Conferência, prévia de totais, envio ao Horizonte (financeiro) e retorno.
// O título chega ao Horizonte pendente de aprovação; só depois de aprovado lá o item conta como faturado aqui.
export function BillingPage() {
  const qc = useQueryClient();
  const [queue, setQueue] = useState<BillingQueue | 'all'>('pendente_conferencia');
  const [clientId, setClientId] = useState('');
  const [competence, setCompetence] = useState('');
  const [invoicing, setInvoicing] = useState<BillingItem | null>(null);
  const exportQueue = queue === 'pendente_conferencia' || queue === 'pronto_envio' || queue === 'faturado' ? queue : queue === 'all' ? 'all' : 'nao_faturados';
  const clients = useQuery({ queryKey: ['clients', 'active-min'], queryFn: () => api.get<Paged<Client>>('/clients?pageSize=200') });
  const q = useQuery({
    queryKey: ['billing-items', queue, clientId, competence],
    queryFn: () => api.get<ListResponse>(`/billing-items${qs({ queue, clientId, competence, pageSize: 100 })}`),
    placeholderData: (prev) => prev,
  });
  const finance = useQuery({ queryKey: ['finance-status'], queryFn: () => api.get<{ enabled: boolean; sendBilling: boolean; linkedClients: number }>('/finance/status') });
  const [results, setResults] = useState<TransferResult[] | null>(null);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['billing-items'] });
    void qc.invalidateQueries({ queryKey: ['alerts-summary'] });
  };
  const send = useMutation({
    mutationFn: (itemIds: string[]) => api.post<{ results: TransferResult[] }>('/billing-transfers', { itemIds }),
    onSuccess: (r) => {
      setResults(r.results);
      refresh();
    },
  });
  const pull = useMutation({
    mutationFn: () => api.post<{ consultados: number; aprovados: number; aguardandoAprovacao: number; reprovados: number; recebidos: number; cancelados: number }>('/finance/receipts/sync'),
    onSuccess: refresh,
  });
  const readyIds = (q.data?.data ?? []).filter((i) => i.reviewStatus === 'pronto_envio' && i.billingStatus === 'nao_faturado' && ['nao_enviado', 'falhou'].includes(i.transportStatus)).map((i) => i.id);
  const review = useMutation({
    mutationFn: (i: BillingItem) => api.post(`/billing-items/${i.id}/review`, { version: i.version, ready: i.reviewStatus !== 'pronto_envio' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['billing-items'] }),
  });

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Faturamento e integração</h1>
          <p>Itens faturáveis gerados pelas regras de cada contrato. Gerar ou conferir não envia nada ao financeiro.</p>
        </div>
      </div>
      <HowTo id="faturamento" title="Como faturar em 3 passos">
        <ol>
          <li>Aba <strong>Pendente de conferência</strong>: confira cliente, horas e valor › <strong>Conferido — pronto para envio</strong>.</li>
          <li>Aba <strong>Pronto para envio</strong>: emita a nota no seu sistema › <strong>Registrar faturamento manual</strong> › informe o número da nota.</li>
          <li>O item vai para <strong>Faturado</strong> e o valor fica travado. Faturado não significa recebido.</li>
        </ol>
        <table>
          <thead>
            <tr><th>O que gera cobrança aqui</th><th>Quando</th></tr>
          </thead>
          <tbody>
            <tr><td>Hora avulsa</td><td>Ao confirmar o atendimento (horas × valor da hora)</td></tr>
            <tr><td>Compra ou ampliação de banco de horas</td><td>Ao registrar a contratação ou &ldquo;Adicionar horas&rdquo;</td></tr>
            <tr><td>Excedente do banco</td><td>Só se você escolher &ldquo;Faturar avulso&rdquo; no atendimento</td></tr>
            <tr><td>Valor fechado (parcelas)</td><td>Ao gerar o item na parcela do contrato</td></tr>
          </tbody>
        </table>
      </HowTo>
      {finance.data?.sendBilling ? (
        <div className="alert alert-info small">
          <strong>Integração com o Horizonte ativa</strong>
          Itens conferidos (“Pronto para envio”) chegam ao Horizonte como <strong>recebimentos propostos</strong>, que só viram conta a receber quando alguém
          aprova lá. Aprovados contam como faturados aqui; reprovados voltam para conferência com o motivo. Recebimento é confirmado pelo Horizonte. Envio repetido
          nunca duplica.
          <div className="actions" style={{ marginTop: '0.5rem' }}>
            <button className="btn btn-sm" disabled={pull.isPending} onClick={() => pull.mutate()}>
              {pull.isPending ? 'Consultando…' : 'Atualizar retorno do Horizonte'}
            </button>
            {pull.data && (
              <span className="small">
                {pull.data.consultados} consultado(s): {pull.data.aprovados} aprovado(s), {pull.data.aguardandoAprovacao} aguardando aprovação, {pull.data.reprovados} reprovado(s),{' '}
                {pull.data.recebidos} recebido(s).
              </span>
            )}
          </div>
        </div>
      ) : (
        <div className="alert alert-info small">
          <strong>Envio de cobranças ao Horizonte desligado</strong>Um administrador liga em Configurações › Integrações. Enquanto isso, exporte o CSV de
          transferência ou use “Registrar faturamento manual”. Exportar não marca nada como enviado. Faturado não significa recebido.
        </div>
      )}

      <div className="segmented queue-tabs" role="tablist" aria-label="Filas" style={{ marginBottom: '0.75rem', flexWrap: 'wrap' }}>
        {(Object.keys(QUEUE_LABEL) as BillingQueue[]).map((k) => (
          <button key={k} role="tab" aria-pressed={queue === k} aria-selected={queue === k} onClick={() => setQueue(k)}>
            {QUEUE_LABEL[k]} {q.data?.queues && <span className="badge" style={{ marginLeft: 4 }}>{q.data.queues[k]}</span>}
          </button>
        ))}
        <button role="tab" aria-pressed={queue === 'all'} onClick={() => setQueue('all')}>
          Todos
        </button>
      </div>
      <div className="toolbar">
        <label className="visually-hidden" htmlFor="bi-client">Cliente</label>
        <select id="bi-client" value={clientId} onChange={(e) => setClientId(e.target.value)}>
          <option value="">Todos os clientes</option>
          {clients.data?.data.map((c) => (
            <option key={c.id} value={c.id}>
              {c.tradeName || c.name}
            </option>
          ))}
        </select>
        <label className="visually-hidden" htmlFor="bi-comp">Competência</label>
        <input id="bi-comp" type="month" value={competence} onChange={(e) => setCompetence(e.target.value)} style={{ flex: '0 1 200px' }} aria-label="Competência" />
      </div>

      <div className="actions" style={{ marginBottom: '0.75rem' }}>
        <a className="btn btn-sm" href={`/api/v1/billing-items/export.csv${qs({ format: 'canonico', queue: exportQueue, clientId, competence })}`}>
          Exportar CSV de transferência (canônico)
        </a>
        <a className="btn btn-sm btn-ghost" href={`/api/v1/billing-items/export.csv${qs({ format: 'br', queue: exportQueue, clientId, competence })}`}>
          CSV para Excel (brasileiro)
        </a>
      </div>
      {finance.data?.sendBilling && readyIds.length > 0 && (
        <div className="actions" style={{ marginBottom: '0.75rem' }}>
          <button className="btn btn-primary" disabled={send.isPending} onClick={() => window.confirm(`Enviar ${readyIds.length} item(ns) conferido(s) ao Horizonte?`) && send.mutate(readyIds)}>
            {send.isPending ? 'Enviando…' : `Enviar ao Horizonte (${readyIds.length})`}
          </button>
        </div>
      )}
      {results && (
        <div className={`alert ${results.every((r) => r.ok) ? 'alert-success' : 'alert-warning'}`}>
          <strong>
            {results.filter((r) => r.ok).length} de {results.length} enviado(s) ao Horizonte
          </strong>
          <ul style={{ margin: '0.25rem 0 0', paddingLeft: '1.1rem' }}>
            {results.map((r) => {
              const item = q.data?.data.find((i) => i.id === r.itemId);
              return (
                <li key={r.itemId} className="small">
                  {item ? `${item.clientName} · ${money(item.total)}` : r.itemId.slice(0, 8)}: {r.message}
                  {r.reference ? ` (${r.reference})` : ''}
                </li>
              );
            })}
          </ul>
          <button className="btn btn-sm btn-ghost" onClick={() => setResults(null)}>
            Fechar
          </button>
        </div>
      )}
      <ErrorAlert error={q.error ?? review.error ?? send.error ?? pull.error} />
      {q.isLoading ? (
        <Loading />
      ) : (
        <div className="grid grid-2" style={{ gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)' }}>
          <div>
            {q.data?.data.length === 0 ? (
              <Empty>Nenhum item nesta fila.</Empty>
            ) : (
              <div className="list">
                {q.data?.data.map((i) => (
                  <div key={i.id} className="list-item">
                    <div className="title" style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <span>
                        {i.clientName} <span className="badge">{SOURCE_LABEL[i.sourceType]}</span>
                      </span>
                      <strong>{money(i.total)}</strong>
                    </div>
                    <div className="small" style={{ marginTop: 2 }}>{i.description}</div>
                    <div className="meta">
                      <span>Competência {i.competence.slice(5)}/{i.competence.slice(0, 4)}</span>
                      {i.serviceDate && <span>{date(i.serviceDate)}</span>}
                      <span>
                        {i.unit === 'hour' ? `${hm(i.durationMinutes)} × ${money(i.unitPrice)}` : `${Number(i.quantity).toLocaleString('pt-BR')} × ${money(i.unitPrice)}`}
                      </span>
                      <span>Contrato {i.contractCode}</span>
                      <span>v{i.version}</span>
                      <span>
                        {i.transportStatus === 'enviado' && i.billingStatus === 'nao_faturado' ? (
                          <span className="badge badge-demo">aguardando aprovação no Horizonte</span>
                        ) : (
                          <>
                            Transporte: {TRANSPORT_LABEL[i.transportStatus]} · Financeiro: {BILLING_LABEL[i.billingStatus]}
                            {i.receiptStatus === 'recebido' && ' · recebido'}
                          </>
                        )}
                      </span>
                      {i.externalReference && <span>Ref.: {i.externalReference}</span>}
                    </div>
                    {i.transportError && (
                      <div className="small" style={{ color: 'var(--danger)', marginTop: 4 }}>
                        {i.transportError}
                      </div>
                    )}
                    <div className="actions" style={{ marginTop: 8 }}>
                      <Link className="btn btn-sm btn-ghost" to={originLink(i)}>
                        Ver origem
                      </Link>
                      {finance.data?.sendBilling && i.reviewStatus === 'pronto_envio' && i.billingStatus === 'nao_faturado' && ['nao_enviado', 'falhou'].includes(i.transportStatus) && (
                        <button className="btn btn-sm btn-primary" disabled={send.isPending} onClick={() => send.mutate([i.id])}>
                          {i.transportStatus === 'falhou' ? 'Tentar enviar de novo' : 'Enviar ao Horizonte'}
                        </button>
                      )}
                      {i.billingStatus === 'nao_faturado' && i.transportStatus === 'falhou' && (
                        <button className="btn btn-sm" disabled={review.isPending} onClick={() => review.mutate({ ...i, reviewStatus: 'pronto_envio' })}>
                          Reabrir conferência
                        </button>
                      )}
                      {i.billingStatus === 'nao_faturado' && i.transportStatus === 'nao_enviado' && (
                        <>
                          <button className="btn btn-sm" disabled={review.isPending} onClick={() => review.mutate(i)}>
                            {i.reviewStatus === 'pronto_envio' ? 'Reabrir conferência' : 'Conferido — pronto para envio'}
                          </button>
                          <button className="btn btn-sm" onClick={() => setInvoicing(i)}>
                            Registrar faturamento manual
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <aside className="card">
            <h2>Prévia dos totais</h2>
            <p className="small muted">{QUEUE_LABEL[queue as BillingQueue] ?? 'Todos os itens'} com os filtros aplicados.</p>
            <table className="table">
              <tbody>
                {q.data?.totals.byClient.map((r) => (
                  <tr key={r.clientId}>
                    <td>
                      {r.clientName} <span className="small muted">({r.n})</span>
                    </td>
                    <td style={{ textAlign: 'right' }}>{money(r.total)}</td>
                  </tr>
                ))}
                <tr>
                  <td>
                    <strong>Total</strong> <span className="small muted">({q.data?.totals.count ?? 0})</span>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <strong>{money(q.data?.totals.total ?? '0')}</strong>
                  </td>
                </tr>
              </tbody>
            </table>
          </aside>
        </div>
      )}
      {invoicing && <ManualInvoiceDialog item={invoicing} onClose={() => setInvoicing(null)} />}
    </>
  );
}

function ManualInvoiceDialog({ item, onClose }: { item: BillingItem; onClose: () => void }) {
  const qc = useQueryClient();
  const [ref, setRef] = useState('');
  const m = useMutation({
    mutationFn: () => api.post(`/billing-items/${item.id}/manual-invoice`, { externalReference: ref, version: item.version }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['billing-items'] });
      onClose();
    },
  });
  return (
    <Dialog title="Registrar faturamento manual" onClose={onClose}>
      <p>
        {item.clientName} · <strong>{money(item.total)}</strong>
      </p>
      <p className="small muted">
        Use somente quando o faturamento já foi emitido fora do aplicativo. Depois de registrado, os valores deste item ficam bloqueados; correções passam a
        depender de ajuste no financeiro. Isso não registra recebimento.
      </p>
      <ErrorAlert error={m.error} />
      <Field label="Referência do faturamento (nota/fatura)" required>
        {(p) => <input {...p} value={ref} onChange={(e) => setRef(e.target.value)} />}
      </Field>
      <div className="actions" style={{ marginTop: '1rem' }}>
        <button className="btn btn-primary" disabled={ref.trim().length < 2 || m.isPending} onClick={() => m.mutate()}>
          Confirmar registro
        </button>
        <button className="btn" onClick={onClose}>
          Cancelar
        </button>
      </div>
    </Dialog>
  );
}
